(() => {
  const selectRandom = (options) => {
    if (!Array.isArray(options) || options.length === 0) return undefined;
    const pool = options.filter((option) => typeof option === 'string' && option.trim() !== '');
    if (pool.length === 0) return undefined;
    const index = Math.floor(Math.random() * pool.length);
    return pool[index];
  };

  const parseOptions = (raw) => {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const sanitized = parsed
          .filter((value) => typeof value === 'string' && value.trim() !== '')
          .map((value) => value.trim());
        return Array.from(new Set(sanitized));
      }
    } catch (_error) {
      /* ignore parse errors */
    }
    return [];
  };

  const removePendingState = (video) => {
    video.classList.remove('banner__video--pending');
  };

  const parseManifest = (raw) => {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.filter((entry) => entry && typeof entry.src === 'string');
      }
    } catch (_error) {
      /* ignore parse errors */
    }
    return [];
  };

  const getPosterForSrc = (manifest, src) => {
    if (!Array.isArray(manifest) || manifest.length === 0) return '';
    if (typeof src !== 'string' || src.trim() === '') return '';
    const match = manifest.find(
      (entry) =>
        entry &&
        typeof entry.src === 'string' &&
        entry.src.trim() === src.trim() &&
        typeof entry.poster === 'string' &&
        entry.poster.trim() !== ''
    );
    return match ? match.poster.trim() : '';
  };

  const applyPosterForSrc = (video, manifest, src) => {
    const poster = getPosterForSrc(manifest, src);
    if (poster) {
      video.setAttribute('poster', poster);
      video.dataset.posterReady = 'true';
    } else if (!video.getAttribute('poster')) {
      video.dataset.posterReady = 'false';
    }
  };

  const attemptPlayback = (video) => {
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  };

  const waitForReadyState = (video, callback) => {
    if (typeof callback !== 'function') return;
    if (video.readyState >= 2) {
      callback();
      return;
    }

    const handleLoaded = () => {
      video.removeEventListener('loadeddata', handleLoaded);
      callback();
    };

    video.addEventListener('loadeddata', handleLoaded);
  };

  const finalizeVideo = (video) => {
    removePendingState(video);
    attemptPlayback(video);
  };

  const ensureSourceElement = (video) => {
    let source = video.querySelector('source');
    if (source) return source;
    source = document.createElement('source');
    source.setAttribute('type', 'video/mp4');
    video.appendChild(source);
    return source;
  };

  const setVideoSource = (video, sourceElement, src, fallback, manifest) => {
    if (!src) {
      removePendingState(video);
      return;
    }

    const handleError = () => {
      video.removeEventListener('error', handleError);
      if (fallback && fallback !== sourceElement.getAttribute('src')) {
        sourceElement.setAttribute('src', fallback);
        applyPosterForSrc(video, manifest, fallback);
        video.load();
        waitForReadyState(video, () => finalizeVideo(video));
      } else {
        removePendingState(video);
      }
    };

    if (fallback) {
      video.addEventListener('error', handleError, { once: true });
    }

    if (sourceElement.getAttribute('src') !== src) {
      sourceElement.setAttribute('src', src);
      applyPosterForSrc(video, manifest, src);
      video.load();
    }

    waitForReadyState(video, () => finalizeVideo(video));
  };

  const applyRandomVideo = (video) => {
    const options = parseOptions(video.dataset.randomVideoOptions);
    const fallback = (video.dataset.randomVideoFallback || '').trim();
    const manifest = parseManifest(video.dataset.randomVideoManifest);
    const source = ensureSourceElement(video);
    if (!source) {
      removePendingState(video);
      return;
    }

    const currentSrc = source.getAttribute('src') ? source.getAttribute('src').trim() : '';
    const pool = Array.from(
      new Set(
        [...options, fallback, currentSrc]
          .filter((value) => typeof value === 'string' && value.trim() !== '')
          .map((value) => value.trim())
      )
    );
    const initialSrc = (video.dataset.randomVideoInitial || '').trim();
    let targetSrc = '';
    if (initialSrc && pool.includes(initialSrc)) {
      targetSrc = initialSrc;
      video.dataset.randomVideoInitial = '';
    } else {
      targetSrc = selectRandom(pool) || initialSrc || fallback || currentSrc;
    }

    if (!targetSrc) {
      removePendingState(video);
      return;
    }

    applyPosterForSrc(video, manifest, targetSrc);

    if (targetSrc === currentSrc) {
      waitForReadyState(video, () => finalizeVideo(video));
      return;
    }

    setVideoSource(video, source, targetSrc, fallback, manifest);
  };

  const lazyVideoCleanup = new WeakMap();

  const loadLazyVideoImmediately = (video) => {
    if (!video || video.dataset.randomVideoLoadState === 'loaded') return;
    const cleanup = lazyVideoCleanup.get(video);
    if (typeof cleanup === 'function') {
      cleanup();
      lazyVideoCleanup.delete(video);
    }
    video.dataset.randomVideoLoadState = 'loaded';
    applyRandomVideo(video);
  };

  const getLazyIntersectionObserver = (() => {
    let observer;
    return () => {
      if (observer) return observer;
      if (!('IntersectionObserver' in window)) return null;
      observer = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            obs.unobserve(entry.target);
            loadLazyVideoImmediately(entry.target);
          });
        },
        { rootMargin: '200px', threshold: 0.1 }
      );
      return observer;
    };
  })();

  const queueLazyVideoLoad = (video) => {
    if (!video) return;
    const loadState = video.dataset.randomVideoLoadState;
    if (loadState === 'queued' || loadState === 'loaded') return;
    video.dataset.randomVideoLoadState = 'queued';

    const observer = getLazyIntersectionObserver();

    let fallbackTimer;
    let fallback;
    const cleanup = () => {
      window.removeEventListener('load', fallback);
      window.removeEventListener('scroll', fallback);
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
        fallbackTimer = undefined;
      }
    };

    fallback = () => {
      if (video.dataset.randomVideoLoadState === 'loaded') {
        cleanup();
        return;
      }
      cleanup();
      if (observer) {
        observer.unobserve(video);
      }
      loadLazyVideoImmediately(video);
    };

    if (observer) {
      observer.observe(video);
    }

    window.addEventListener('load', fallback, { once: true });
    window.addEventListener('scroll', fallback, { once: true });
    fallbackTimer = window.setTimeout(() => fallback(), 20000);
    lazyVideoCleanup.set(video, cleanup);
  };

  const initRandomVideos = (scope = document) => {
    scope.querySelectorAll('[data-random-video-options]').forEach((video) => {
      if (video.dataset.lazyLoad === 'true') {
        queueLazyVideoLoad(video);
      } else {
        applyRandomVideo(video);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initRandomVideos());
  } else {
    initRandomVideos();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initRandomVideos(event.target);
  });
})();
